// Org switcher dropdown for the navbar (issue #194).
//
// Renders the active org's name as the trigger; the menu lists every org
// the user is a member of (with role badge) plus a "Create organization"
// item that links to /create-org. Selecting a row sets the active org
// in context (synchronous; no network round-trip).

import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useOrganization } from "../hooks/useOrganization";

export default function OrgSwitcher() {
  const { t } = useTranslation("navbar");
  const {
    memberships,
    activeOrganization,
    activeRole,
    setActiveOrganizationId,
    loading,
  } = useOrganization();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  if (loading && memberships.length === 0) {
    return (
      <span className="org-switcher org-switcher--loading" aria-busy="true">
        {t("orgLoading")}
      </span>
    );
  }

  // Even when the user has no memberships we render the trigger as a link
  // to /create-org so they can create their first one. The personal org is
  // backfilled for every legacy user (issue #194 migration), so this
  // branch should only hit for brand-new accounts.
  if (memberships.length === 0) {
    return (
      <Link
        to="/create-org"
        className="org-switcher org-switcher--empty dropdown-trigger"
      >
        {t("orgCreateFirst")}
      </Link>
    );
  }

  const triggerLabel = activeOrganization?.name ?? t("orgPickOne");

  return (
    <div className="org-switcher dropdown" ref={ref}>
      <button
        className="dropdown-trigger org-switcher-trigger"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("orgSwitcherAria")}
      >
        <span className="org-switcher-label">{triggerLabel}</span>
        {activeRole ? (
          <span className={`org-role-badge org-role-badge--${activeRole}`}>
            {t(`role.${activeRole}`)}
          </span>
        ) : null}
        <span className="dropdown-caret">&#9662;</span>
      </button>
      {open && (
        <div
          className="dropdown-menu dropdown-menu--left org-switcher-menu"
          role="listbox"
          onClick={() => setOpen(false)}
        >
          {memberships.map((m) => (
            <button
              key={m.organization_id}
              type="button"
              className={`dropdown-item org-switcher-item${
                m.organization_id === activeOrganization?.id
                  ? " org-switcher-item--active"
                  : ""
              }`}
              role="option"
              aria-selected={m.organization_id === activeOrganization?.id}
              onClick={() => setActiveOrganizationId(m.organization_id)}
            >
              <span className="org-switcher-item-name">
                {m.organization?.name ?? m.organization_id}
              </span>
              <span className={`org-role-badge org-role-badge--${m.role}`}>
                {t(`role.${m.role}`)}
              </span>
            </button>
          ))}
          <div className="dropdown-divider" />
          <Link to="/create-org" className="dropdown-item">
            <span className="dropdown-plus">+</span> {t("orgCreate")}
          </Link>
        </div>
      )}
    </div>
  );
}
